import { bus } from "@/boot/bus";
import { useGraphql } from "@/core/graphql/service";
import pushSubscribeMutation from "@/gql/gen/mutation/pushSubscribeMutation";
import pushUnsubscribeMutation from "@/gql/gen/mutation/pushUnsubscribeMutation";
import pushVapidPublicKeyQuery from "@/gql/gen/query/pushVapidPublicKeyQuery";
import type { AppClient } from "@/gql/gen/schema";
import { defineStore } from "pinia";
import { Notify } from "quasar";
import { computed, ref } from "vue";

export interface PushSubscription {
  endpoint: string;
  p256dhKey: string;
  authToken: string;
}

export interface PushState {
  isSubscribed: boolean;
  endpoint: string | null;
  isLoading: boolean;
  error: string | null;
}

const PUSH_SUBSCRIPTION_KEY = "push_subscription_status";
const PUSH_EMULATION_KEY = "push_emulation_subscription";

// Глобальный флаг для включения режима эмуляции
// Установите window.ENABLE_PUSH_EMULATION = true для разработки на HTTP
declare global {
  interface Window {
    ENABLE_PUSH_EMULATION?: boolean;
  }
}

export const usePushStore = defineStore("push", () => {
  const { useMutation, responseSelection } = useGraphql();

  // Создаем mutation handlers для новых мутаций
  const subscribeRequest = useMutation(
    pushSubscribeMutation({
      payload: {
        appClient: {
          ID: true,
          WEB_PUSH_ENDPOINT: true,
          WEB_PUSH_P256DH: true,
          WEB_PUSH_AUTH: true,
          WEB_PUSH_ACTIVE: true,
        },
      },
      ...responseSelection(),
    })
  );

  const unsubscribeRequest = useMutation(
    pushUnsubscribeMutation({
      payload: {
        appClient: {
          ID: true,
          WEB_PUSH_ACTIVE: true,
        },
      },
      ...responseSelection(),
    })
  );

  // State - обновленный интерфейс согласно требованиям
  const isSubscribed = ref(false);
  const endpoint = ref<string | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Дополнительные поля для совместимости
  const isSupported = ref(false);
  const subscription = ref<PushSubscription | null>(null);
  const vapidPublicKey = ref<string | null>(null);
  const permission = ref<NotificationPermission>("default");
  const isEmulationMode = ref(false);

  // Getters
  const canSubscribe = computed(() => {
    return (
      isSupported.value && permission.value !== "denied" && !isSubscribed.value
    );
  });

  const isDenied = computed(() => {
    return permission.value === "denied";
  });

  const needsPermission = computed(() => {
    return permission.value === "default";
  });

  // Actions
  /**
   * Инициализировать состояние из AppClient данных
   * Вызывается при загрузке scopeQuery
   * Requirements: 6.1
   */
  function initFromAppClient(appClient: AppClient | null): void {
    if (!appClient) {
      isSubscribed.value = false;
      endpoint.value = null;
      return;
    }

    // Определяем статус подписки на основе WEB_PUSH_ACTIVE
    isSubscribed.value = appClient.WEB_PUSH_ACTIVE === true;
    endpoint.value = appClient.WEB_PUSH_ENDPOINT || null;

    // Обновляем subscription объект для совместимости
    if (appClient.WEB_PUSH_ENDPOINT && appClient.WEB_PUSH_P256DH && appClient.WEB_PUSH_AUTH) {
      subscription.value = {
        endpoint: appClient.WEB_PUSH_ENDPOINT,
        p256dhKey: appClient.WEB_PUSH_P256DH,
        authToken: appClient.WEB_PUSH_AUTH,
      };
    } else {
      subscription.value = null;
    }

    console.log("[Push Store] Initialized from AppClient", {
      isSubscribed: isSubscribed.value,
      endpoint: endpoint.value,
      webPushActive: appClient.WEB_PUSH_ACTIVE,
    });
  }

  /**
   * Проверить поддержку push уведомлений в браузере
   * Requirements: 6.4
   */
  function checkBrowserSupport(): boolean {
    if (typeof window === "undefined") return false;

    return (
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    );
  }

  /**
   * Подписаться на push уведомления
   * Запрашивает разрешение браузера и вызывает GraphQL мутацию
   * Requirements: 6.2
   */
  async function subscribe(): Promise<void> {
    if (isLoading.value) return;

    // Проверить поддержку
    if (!checkBrowserSupport()) {
      error.value = 'Push уведомления не поддерживаются в вашем браузере';
      return;
    }

    isLoading.value = true;
    error.value = null;

    // Таймаут для защиты от зависания
    const timeoutId = setTimeout(() => {
      if (isLoading.value) {
        isLoading.value = false;
        error.value = 'Превышено время ожидания ответа сервера';
        Notify.create({
          type: "negative",
          message: "Превышено время ожидания ответа сервера",
          position: "top",
        });
      }
    }, 30000); // 30 секунд таймаут

    try {
      console.log('11')
      // Запросить разрешение
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        throw new Error('Разрешение на уведомления отклонено');
      }
      console.log('222')
      // Получить подписку от браузера через Quasar Service Worker
      let registration: ServiceWorkerRegistration;
      
      // Попробуем получить регистрацию из Quasar
      if (typeof window !== 'undefined' && (window as any).__SW_REGISTRATION__) {
        registration = (window as any).__SW_REGISTRATION__;
      } else {
        // Fallback - ждем готовности Service Worker
        registration = await navigator.serviceWorker.ready;
      }
      console.log('333')
      // Получить VAPID ключ если нужно
      if (!vapidPublicKey.value) {
        console.log('444')
        await fetchVapidPublicKey();
      }
      console.log('555')

      if (!vapidPublicKey.value) {
        console.log('666')
        throw new Error('VAPID ключ недоступен');
      }
      console.log('777')

      const browserSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey.value) as BufferSource
      });
      console.log('88')
      const subscriptionData = parseSubscription(browserSubscription);

      // Отправить на сервер через новую мутацию с таймаутом
      const result = await Promise.race([
        subscribeRequest.mutate({
          endpoint: subscriptionData.endpoint,
          p256dhKey: subscriptionData.p256dhKey,
          authToken: subscriptionData.authToken,
        }),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Таймаут запроса к серверу')), 25000)
        )
      ]);

      if (result?.payload?.appClient) {
        // Обновить состояние из ответа сервера
        initFromAppClient(result.payload.appClient);

        Notify.create({
          type: "positive",
          message: "Вы успешно подписались на уведомления!",
          position: "top",
        });
      } else {
        throw new Error('Сервер не вернул данные AppClient');
      }

    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Не удалось подписаться на уведомления';

      error.value = errorMessage;

      Notify.create({
        type: "negative",
        message: errorMessage,
        position: "top",
      });

      console.error('Push subscription error:', err);

    } finally {
      clearTimeout(timeoutId);
      isLoading.value = false;
    }
  }

  /**
   * Отписаться от push уведомлений
   * Requirements: 6.3
   */
  async function unsubscribe(): Promise<void> {
    if (isLoading.value) return;

    isLoading.value = true;
    error.value = null;

    // Таймаут для защиты от зависания
    const timeoutId = setTimeout(() => {
      if (isLoading.value) {
        isLoading.value = false;
        error.value = 'Превышено время ожидания ответа сервера';
        Notify.create({
          type: "negative",
          message: "Превышено время ожидания ответа сервера",
          position: "top",
        });
      }
    }, 30000); // 30 секунд таймаут

    try {
      const result = await Promise.race([
        unsubscribeRequest.mutate({}),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Таймаут запроса к серверу')), 25000)
        )
      ]);

      if (result?.payload?.appClient) {
        // Обновить состояние из ответа сервера
        initFromAppClient(result.payload.appClient);

        Notify.create({
          type: "info",
          message: "Вы отписались от уведомлений",
          position: "top",
        });
      } else {
        throw new Error('Сервер не вернул данные AppClient');
      }

    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Не удалось отписаться от уведомлений';

      error.value = errorMessage;

      Notify.create({
        type: "negative",
        message: errorMessage,
        position: "top",
      });

      console.error('Push unsubscribe error:', err);

    } finally {
      clearTimeout(timeoutId);
      isLoading.value = false;
    }
  }

  /**
   * Получить VAPID публичный ключ с сервера
   */
  async function fetchVapidPublicKey() {
    try {
      const result = await pushVapidPublicKeyQuery(true).run();

      if (result) {
        vapidPublicKey.value = result;
        console.log("[Push Store] VAPID key fetched");
      }
    } catch (error) {
      console.error("[Push Store] Failed to fetch VAPID key:", error);
    }
  }

  /**
   * Парсинг PushSubscription в наш формат
   */
  function parseSubscription(
    sub: globalThis.PushSubscription
  ): PushSubscription {
    const keys = sub.toJSON();

    return {
      endpoint: sub.endpoint,
      p256dhKey: keys.keys?.p256dh || "",
      authToken: keys.keys?.auth || "",
    };
  }

  /**
   * Конвертация VAPID ключа из base64url в Uint8Array
   */
  function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, "+")
      .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }

  /**
   * Конвертация ArrayBuffer в base64
   */
  function arrayBufferToBase64(buffer: ArrayBuffer | null | undefined): string {
    if (!buffer) return "";

    const bytes = new Uint8Array(buffer);
    let binary = "";

    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i] ?? 0);
    }

    return window.btoa(binary);
  }

  /**
   * Генерация случайного токена для эмуляции
   */
  function generateRandomToken(length: number): string {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Открыть диалог подписки на уведомления через Vue Router
   */
  function showSubscribeDialog(): void {
    bus.emit("router:push", "/push-subscribe");
  }

  return {
    // State - обновленный интерфейс PushState
    isSubscribed,
    endpoint,
    isLoading,
    error,

    // Дополнительные поля для совместимости
    isSupported,
    subscription,
    vapidPublicKey,
    permission,
    isEmulationMode,

    // Getters
    canSubscribe,
    isDenied,
    needsPermission,

    // Actions - новые методы согласно требованиям
    initFromAppClient,
    checkBrowserSupport,
    subscribe,
    unsubscribe,

    // Вспомогательные методы
    fetchVapidPublicKey,
    parseSubscription,
    urlBase64ToUint8Array,
    arrayBufferToBase64,
    generateRandomToken,
    showSubscribeDialog,
  };
});
